<template>
  <div class="market-table">
    <div :class="{'hide-sort': !showSort}" class="market-table__header">
      <button class="btn btn-transparent header-cell" type="button">
        {{ t('market') }}
      </button>
      <button
          class="btn btn-transparent header-cell"
          type="button"
          @click="setSort(MarketOrderByCol.Rating)"
      >
        {{ t('rating') }}
        <SortIcon
            v-if="showSort"
            :is-active="activeSort === MarketOrderByCol.Rating"
            :sort-by="sortBy"
            default-color="#92A1BF"
        />
      </button>
      <button
          class="btn btn-transparent header-cell"
          type="button"
          @click="setSort(MarketOrderByCol.UniqueItems)"
      >
        {{ t('unique_items') }}
        <SortIcon
            v-if="showSort"
            :is-active="activeSort === MarketOrderByCol.UniqueItems"
            :sort-by="sortBy"
            default-color="#92A1BF"
        />
      </button>
      <button
          class="btn btn-transparent header-cell"
          type="button"
          @click="setSort(MarketOrderByCol.Offers)"
      >
        {{ t('offers') }}
        <SortIcon
            v-if="showSort"
            :is-active="activeSort === MarketOrderByCol.Offers"
            :sort-by="sortBy"
            default-color="#92A1BF"
        />
      </button>
      <button
          class="btn btn-transparent header-cell"
          type="button"
          @click="setSort(MarketOrderByCol.ItemsValue)"
      >
        {{ t('items_value') }}
        <SortIcon
            v-if="showSort"
            :is-active="activeSort === MarketOrderByCol.ItemsValue"
            :sort-by="sortBy"
            default-color="#92A1BF"
        />
      </button>
      <button
          class="btn btn-transparent header-cell"
          type="button"
          @click="setSort(MarketOrderByCol.AvgDiscount)"
      >
        {{ t('avg_discount') }}
        <SortIcon
            v-if="showSort"
            :is-active="activeSort === MarketOrderByCol.AvgDiscount"
            :sort-by="sortBy"
            default-color="#92A1BF"
        />
      </button>
      <button
          class="btn btn-transparent header-cell"
          type="button"
          @click="setSort(MarketOrderByCol.MonthlyVisit)"
      >
        {{ t('monthly_visits') }}
        <SortIcon
            v-if="showSort"
            :is-active="activeSort === MarketOrderByCol.MonthlyVisit"
            :sort-by="sortBy"
            default-color="#92A1BF"
        />
      </button>
    </div>

    <div class="f market-table-sort-select-wrapper">
      <UiSelect
          :items="sortItems"
          class="market-table-sort-select"
          clearable
          label-key="label"
          @on-change="setSelectSort"
      />
      <ui-input
          v-model="searchTerm"
          :placeholder="t('search')"
          show-search-icon
      />
    </div>

    <div class="market-table__body">
      <template v-if="computedMarkets.length">
        <div
            v-for="market in computedMarkets"
            :key="market.market_full_name"
            class="market-row"
        >
          <div class="market-cell market-info">
            <img
                :alt="market.market_full_name"
                :src="getImageFromProxy(`${imagesUrl}markets/${market.market_name}.svg`, '100:100')"
                class="market-icon"
            >
            <div class="market-details">
              <h2 class="market-name">
                <nuxt-link
                    :to="localePath(`/markets/${getModifiedString(market.slug)}`)"
                    class="market-name__link">
                  {{ market.market_full_name }}
                </nuxt-link>
              </h2>
              <p class="market-category">
                {{ t('market_type.' + market.market_type?.toLowerCase()) }}
              </p>
            </div>
          </div>

          <div class="market-cell rating">
            <span class="market-cell__label">{{ t('rating') }}</span>

            <svg :class="{
            green: market.rating >=4,
            yellow: market.rating >= 3 && market.rating < 4,
            red: market.rating < 3
          }"
                 class="star"
                 height="16"
                 width="16"
            >
              <use xlink:href="/icons/sprite.svg#star"></use>
            </svg>
            <span class="reviews">
            {{ market.rating }} | {{ t('reviews_count', {n: getNumberShorts(market.votes)}) }}
          </span>
          </div>

          <div class="market-cell">
            <div class="f fs gap8">
              <svg height="16" width="16">
                <use xlink:href="/icons/sprite.svg#i-basket"></use>
              </svg>
              <span class="market-cell__label">
                {{ t('unique_items') }}
            </span>
            </div>
            <span>
            {{ +getNumberShorts(market.items) === 0 ? 'N/A' : getNumberShorts(market.items) }}
          </span>
          </div>

          <div class="market-cell">

            <div class="f fs gap8">
              <svg height="16" width="16">
                <use xlink:href="/icons/sprite.svg#i-basket"></use>
              </svg>
              <span class="market-cell__label">
                {{ t('offers') }}
            </span>
            </div>
            <span>
              {{ +getNumberShorts(market.offers) === 0 ? 'N/A' : getNumberShorts(market.offers) }}
            </span>
          </div>

          <div class="market-cell">
            <div class="f fs gap8">
              <svg height="16" width="16">
                <use xlink:href="/icons/sprite.svg#i-cash"></use>
              </svg>
              <span class="market-cell__label">
                {{ t('items_value') }}
            </span>
            </div>
            <span>
              {{
                +getNumberShorts(market.price_sum) === 0 ? 'N/A' : getNumberShorts(market.price_sum)
              }}
            </span>
          </div>
          <div class="market-cell">
            <div class="f fs gap8">
              <svg height="16" width="16">
                <use xlink:href="/icons/sprite.svg#i-cash"></use>
              </svg>
              <span class="market-cell__label">
                {{ t('avg_discount') }}
            </span>
            </div>
            <span>-{{ market.avg_discount || 0 }}%</span>
          </div>
          <div class="market-cell visits">
            <div class="f fs gap8">
              <svg height="16" width="16">
                <use xlink:href="/icons/sprite.svg#i-user"></use>
              </svg>
              <span class="market-cell__label">
                {{ t('monthly_visits') }}
            </span>
            </div>
            <span>
               {{
                +getNumberShorts(market.monthly_visit) === 0 ? t('soon') : getNumberShorts(market.monthly_visit)
              }}
            </span>
          </div>
        </div>
      </template>
      <ui-no-result v-else class="mxa" />
    </div>
  </div>
</template>

<script lang="ts" setup>

import SortIcon from "~/components/Icons/Components/SortIcon.vue";
import {MarketInfo, MarketOrderByCol, MarketOrderByOrder} from "~/graphql/generated/schema-types";
import {getNumberShorts} from "~/utils/number-shorts";
import {getImageFromProxy} from "~/utils/get-image-url";

const props = withDefaults(
    defineProps<{
      markets: MarketInfo[],
      showSort: boolean
    }>(),
    {
      markets: () => [] as MarketInfo[],
      showSort: true
    });
type SortType = null | MarketOrderByCol;

const {t} = useI18n()
const route = useRoute()
const router = useRouter()
const localePath = useLocalePath()
const config = useRuntimeConfig()
const imagesUrl = config.public.imagesUrl

const searchTerm = ref('')
const sortBy = ref<MarketOrderByOrder>(route.query.order as MarketOrderByOrder || MarketOrderByOrder.Asc)
const activeSort = ref<SortType>(route.query.sort as SortType || null)
const sortItems = ref([] as {
  label: string;
  sort: MarketOrderByCol;
  order: MarketOrderByOrder;
}[])

Object.values(MarketOrderByCol).forEach((i) => {
  sortItems.value.push({
    label: t(`markets_sort.${i.toLowerCase()}_asc`),
    sort: i as MarketOrderByCol,
    order: MarketOrderByOrder.Asc
  })
  sortItems.value.push({
    label: t(`markets_sort.${i.toLowerCase()}_desc`),
    sort: i as MarketOrderByCol,
    order: MarketOrderByOrder.Desc
  })
})

const setSort = (name: SortType) => {
  if (activeSort.value === name) {
    sortBy.value = sortBy.value === MarketOrderByOrder.Asc ? MarketOrderByOrder.Desc : MarketOrderByOrder.Asc
  } else {
    activeSort.value = name
    sortBy.value = MarketOrderByOrder.Asc
  }

  router.push({
    query: {
      ...route.query,
      sort: name,
      order: sortBy.value
    }
  })
}

const setSelectSort = (e: {
  label: string;
  sort: MarketOrderByCol;
  order: MarketOrderByOrder;
} | null) => {
  if (!e) {
    router.push({
      query: {}
    })
    return
  }
  router.push({
    query: {
      ...route.query,
      sort: e.sort,
      order: e.order
    }
  })
}

const computedMarkets = computed(() => {
  if (!searchTerm.value) {
    return props.markets
  }

  return props.markets.filter(market => {
    return market.market_full_name.toLowerCase().includes(searchTerm.value.toLowerCase())
  })
})
</script>

<style lang="scss" scoped>
@use 'sass:color';

.market-table {
  color: var(--surfaces-on-surface-variable);

  &__header {
    display: grid;
    grid-template-columns: 2fr 1.5fr repeat(5, 1fr);

    &.hide-sort {
      .header-cell {
        pointer-events: none;
      }
    }

    .header-cell {
      padding: 8px 16px 8px 0;
      border-radius: 4px;
      color: #8E9EBD;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      justify-content: flex-start;

      &:hover {
        color: var(--accent-secondary);
        background: unset;

        &:deep(svg) path {
          fill: var(--accent-secondary);
        }
      }

      &:deep(svg) {
        width: 16px;
        height: 16px;
        margin-left: 2px;

        path {
          transition: fill 0.3s;
        }
      }
    }

    @media screen and (max-width: 1000px) {
      display: none;
    }
  }

  &-sort-select-wrapper {
    display: none;

    @media screen and (max-width: 1000px) {
      display: flex;
      gap: 16px;
      margin-bottom: 16px;
    }
    @media screen and (max-width: 600px) {
      flex-direction: column-reverse;
    }
  }

  &__body {
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .market-row {
      display: grid;
      grid-template-columns: 2fr 1.5fr repeat(5, 1fr);
      align-items: center;
      padding: 12px 0;
      background: var(--surfaces-low-container);
      border-radius: 8px;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      cursor: pointer;
      position: relative;

      &:hover {
        background: color-mix(IN srgb, var(--surfaces-low-container) 98%, white);
      }
    }

    @media screen and (max-width: 1000px) {
      padding-top: 0;
      gap: 12px;
      display: grid;
      grid-template-columns:repeat(auto-fill, minmax(250px, 1fr));
      .market-row {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
        padding: 12px 16px;

        .market-cell {
          justify-content: space-between;
          width: 100%;
          padding: 0;

          &__label {
            display: inline;
          }
        }

        .market-info {
          justify-content: flex-start;
          gap: 20px;
        }

        .rating {
          order: 999;
          position: relative;
          padding-top: 16px;

          svg {
            margin-left: auto;
          }

          &:after {
            content: '';
            display: block;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 1px;
            background-image: linear-gradient(
                    to right,
                    #3A3F45 50%,
                    transparent 50%
            );
            background-size: 10px 4px;
            background-repeat: repeat-x;
          }
        }
      }
    }
  }
}

.visits {
  padding-right: 16px;
}

.market-info {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-left: 16px;

  .market-icon {
    width: 32px;
    height: 32px;
    object-position: center;
    object-fit: contain;
  }

  .market-details {
    .market-name {
      color: var(--surfaces-on-surface);
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      margin-bottom: 4px;

      &__link:after {
        content: '';
        position: absolute;
        inset: 0;
      }
    }

    .market-category {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
    }
  }
}

.rating {
  .star {
    color: #2d3139;

    &.green {
      color: var(--accent-primary);
    }

    &.yellow {
      color: var(--on-attention);
    }

    &.red {
      color: var(--destructive);
    }
  }

  .reviews {
    font-size: 14px;
    font-weight: 600;
  }
}

.market-cell {
  display: flex;
  align-items: center;
  gap: 6px;

  &__label {
    display: none;
  }
}
</style>
